import React from 'react'
import { PageShell } from './PageShell'
import { StatCard } from '../components/StatCard'
import { ContactImportWizard } from '../components/ContactImportWizard'
import { AddContactModal, ContactDetailModal } from '../components/ContactModal'
import type { ContactWithStatus } from '../components/ContactModal'
import { PlanLimitBanner } from '../components/PlanLimitBanner'
import { useDashboard } from '../DashboardContext'
import { contactTagPill } from '../utils'
import { Skeleton, SkeletonTableRows } from '../../../components/ui/Skeleton'
import type { Contact } from '../../../types'

export function ContactsPage({ isActive }: { isActive: boolean }) {
  const { contacts, contactsLoading, loadContacts, businessInfo } = useDashboard()

  const [showImportWizard, setShowImportWizard] = React.useState(false)
  const [showAddContactModal, setShowAddContactModal] = React.useState(false)
  const [selectedContact, setSelectedContact] = React.useState<ContactWithStatus | null>(null)

  const lapseDays = businessInfo?.lapseWindowDays ?? 60

  const contactsWithStatus = contacts.map(c => {
    const lastVisit = new Date(c.lastVisitDate)
    const diffDays = Number.isNaN(lastVisit.getTime())
      ? 0
      : Math.floor((Date.now() - lastVisit.getTime()) / (1000 * 60 * 60 * 24))
    const isLapsed = diffDays >= lapseDays
    return { ...c, isLapsed }
  })

  const totalContacts = contactsWithStatus.length
  const lapsedContacts = contactsWithStatus.filter(c => c.isLapsed).length
  const activeContacts = totalContacts - lapsedContacts

  function handleContactSaved(updated: Contact) {
    const lastVisit = new Date(updated.lastVisitDate)
    const diffDays = Number.isNaN(lastVisit.getTime())
      ? 0
      : Math.floor((Date.now() - lastVisit.getTime()) / (1000 * 60 * 60 * 24))
    setSelectedContact({ ...updated, isLapsed: diffDays >= lapseDays })
    void loadContacts()
  }

  return (
    <>
      <PageShell id="page-contacts" isActive={isActive}>
        <div className="content">
          <PlanLimitBanner />
          <div className="stats-row" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
            <StatCard
              className="sc-green"
              delay=".05s"
              iconClass="ic-green"
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                </svg>
              }
              label="Total Contacts"
              value={String(totalContacts)}
              delta={
                <>
                  {contactsLoading ? <Skeleton height={11} width={20} /> : <span className="du">live</span>}
                  <span className="dn">from import</span>
                </>
              }
            />

            <StatCard
              className="sc-teal"
              delay=".1s"
              iconClass="ic-teal"
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 11 12 14 22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              }
              label={`Active (${lapseDays}d)`}
              value={String(activeContacts)}
              delta={
                <>
                  <span style={{ color: 'var(--teal)' }}>
                    {totalContacts > 0
                      ? `${Math.round((activeContacts / totalContacts) * 100)}%`
                      : '0%'}
                  </span>
                  <span className="dn">of total</span>
                </>
              }
            />

            <StatCard
              className="sc-orange"
              delay=".15s"
              iconClass="ic-orange"
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              }
              label="Lapsed"
              value={String(lapsedContacts)}
              delta={
                <>
                  <span className="dd">
                    {totalContacts > 0
                      ? `${Math.round((lapsedContacts / totalContacts) * 100)}%`
                      : '0%'}
                  </span>
                  <span className="dn">of total</span>
                </>
              }
            />
          </div>

          <div className="filter-row">
            <div className="search-bar">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input placeholder="Search contacts…" aria-label="Search contacts" />
            </div>
            <select className="select-input" aria-label="Filter by status">
              <option>All contacts</option>
              <option>Active</option>
              <option>Lapsed</option>
            </select>
            <select className="select-input" aria-label="Filter by type">
              <option>All types</option>
              <option>Regular</option>
              <option>VIP</option>
            </select>
            <button
              className="btn-primary"
              style={{ marginLeft: 'auto' }}
              onClick={() => setShowAddContactModal(true)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add Contact
            </button>
            <button className="btn-secondary" onClick={() => setShowImportWizard(true)}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Upload CSV
            </button>
          </div>

          <div className="panel" aria-busy={contactsLoading} aria-label="Contacts">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Phone</th>
                  <th>Last Visit</th>
                  <th>Visits</th>
                  <th>Tags</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {contactsLoading ? (
                  <SkeletonTableRows cols={6} rows={5} />
                ) : null}
                {!contactsLoading && contactsWithStatus.map((r, idx) => {
                  const tagPill = contactTagPill(r.metadata)
                  return (
                    <tr key={r.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            className="avatar-circle"
                            style={{
                              background: idx % 2 === 0 ? 'var(--accent-bg)' : 'var(--blue-bg)',
                              color: idx % 2 === 0 ? 'var(--accent)' : 'var(--blue)',
                            }}
                          >
                            {r.fullName
                              .split(/\s+/)
                              .filter(Boolean)
                              .slice(0, 2)
                              .map(w => w[0]?.toUpperCase())
                              .join('')}
                          </div>
                          <div>
                            <div className="cust-name">{r.fullName}</div>
                            <div className="cust-sub">{r.email}</div>
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          fontFamily: "'DM Mono',monospace",
                          fontSize: 12,
                          color: 'var(--text2)',
                        }}
                      >
                        {r.phone || '—'}
                      </td>
                      <td
                        style={{
                          fontFamily: "'DM Mono',monospace",
                          fontSize: 12,
                          color: 'var(--text2)',
                        }}
                      >
                        {new Date(r.lastVisitDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </td>
                      <td
                        style={{
                          fontFamily: "'DM Mono',monospace",
                          fontSize: 12,
                          color: 'var(--text2)',
                        }}
                      >
                        {r.metadata?.visits ?? '—'}
                      </td>
                      <td>
                        <span className={tagPill.className}>{tagPill.label}</span>
                      </td>
                      <td>
                        <button className="action-btn" onClick={() => setSelectedContact(r)}>
                          View
                        </button>
                      </td>
                    </tr>
                  )
                })}
                {!contactsLoading && contactsWithStatus.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '36px 16px', color: 'var(--text2)', fontSize: 13 }}>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.35 }} aria-hidden="true">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="8.5" cy="7" r="4" />
                          <line x1="20" y1="8" x2="20" y2="14" />
                          <line x1="23" y1="11" x2="17" y2="11" />
                        </svg>
                        <span>No contacts yet. Use Upload CSV to import your list.</span>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </PageShell>

      <ContactImportWizard
        open={showImportWizard}
        onClose={() => setShowImportWizard(false)}
        onImported={() => { void loadContacts() }}
      />

      <AddContactModal
        open={showAddContactModal}
        onClose={() => setShowAddContactModal(false)}
        onSaved={loadContacts}
      />

      {selectedContact ? (
        <ContactDetailModal
          contact={selectedContact}
          onClose={() => setSelectedContact(null)}
          onSaved={handleContactSaved}
          onDeleted={() => {
            setSelectedContact(null)
            void loadContacts()
          }}
        />
      ) : null}
    </>
  )
}
